import { useCallback, useEffect, useRef, useState } from 'react';
import { _dataProvider } from '../../../dataProvider/DataProvider';
import i18n from '../../../i18n';

interface IUseVoiceRecordingOptions {
  groupId: number;
  messages: any[];
  mapMessage: (messages: any[]) => any[];
  addMessage: (messages: any[]) => void;
  onError: (msg: string) => void;
}

interface IUseVoiceRecordingReturn {
  isRecording: boolean;
  recordingDuration: number;
  isVoiceMode: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  toggleVoiceMode: () => void;
  formatDuration: (milliseconds: number) => string;
}

const MAX_RECORDING_DURATION = 600000;
const MIN_RECORDING_DURATION = 1000;

export const useVoiceRecording = ({
  groupId,
  messages,
  mapMessage,
  addMessage,
  onError
}: IUseVoiceRecordingOptions): IUseVoiceRecordingReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isVoiceMode, setIsVoiceMode] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStartTimeRef = useRef<number>(0);
  const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const stopRecordingRef = useRef<(() => void) | null>(null);
  const isStoppingRef = useRef<boolean>(false);

  const getSupportedMimeType = useCallback((): string | null => {
    const types = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mpeg'];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    if (MediaRecorder.isTypeSupported('')) {
      return '';
    }

    return null;
  }, []);

  const cleanupRecording = useCallback(() => {
    // 重置停止标志
    isStoppingRef.current = false;
    audioChunksRef.current = [];
    setIsRecording(false);
    setRecordingDuration(0);
    recordingStartTimeRef.current = 0;
    mediaRecorderRef.current = null;
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  }, []);

  const stopRecording = useCallback(() => {
    // 防止重复执行
    if (isStoppingRef.current) {
      return;
    }

    const recorder = mediaRecorderRef.current;
    const stream = mediaStreamRef.current;

    if (!isRecording && !recorder) {
      return;
    }

    // 设置停止标志
    isStoppingRef.current = true;

    const cleanup = () => {
      // 重置停止标志
      isStoppingRef.current = false;
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      audioChunksRef.current = [];
      if (stream) {
        try {
          stream.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch {
              // Ignore track stop errors
            }
          });
        } catch {
          // Ignore stream errors
        }
        mediaStreamRef.current = null;
      }
      setIsRecording(false);
      setRecordingDuration(0);
      mediaRecorderRef.current = null;
      recordingStartTimeRef.current = 0;
    };

    if (recorder && recorder.state !== 'inactive') {
      try {
        const originalOnStop = recorder.onstop;
        const cleanupTimeout = setTimeout(() => {
          if (recorder.state !== 'inactive') {
            cleanup();
          } else if (isRecording) {
            cleanup();
          }
        }, 5000);

        recorder.onstop = () => {
          clearTimeout(cleanupTimeout);
          if (originalOnStop) {
            originalOnStop.call(recorder, new Event('stop'));
          }
          cleanup();
        };

        recorder.stop();
      } catch {
        cleanup();
        onError(i18n.t('Chatroom.recordingError'));
      }
    } else {
      cleanup();
    }
  }, [isRecording, onError]);

  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  const startRecording = useCallback(async () => {
    if (isRecording || mediaRecorderRef.current) {
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mimeType = getSupportedMimeType();
      if (!mimeType) {
        throw new Error('No supported audio MIME type found');
      }

      const mediaRecorderOptions: MediaRecorderOptions = {};
      if (mimeType) {
        mediaRecorderOptions.mimeType = mimeType;
      }

      const mediaRecorder = new MediaRecorder(stream, mediaRecorderOptions);

      audioChunksRef.current = [];
      recordingStartTimeRef.current = Date.now();

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = (event) => {
        const error = (event as any).error;
        let errorKey = 'Chatroom.recordingError';

        if (error) {
          if (error.name === 'NotSupportedError') {
            errorKey = 'Chatroom.recordingNotSupported';
          } else if (error.name === 'NotAllowedError') {
            errorKey = 'Chatroom.recordingPermissionDenied';
          } else {
            errorKey = 'Chatroom.recordingError';
          }
        }

        onError(i18n.t(errorKey));
        stopRecordingRef.current?.();
      };

      mediaRecorder.onstop = async () => {
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((track) => track.stop());
          mediaStreamRef.current = null;
        }

        const recorder = mediaRecorderRef.current;
        const blobType = recorder?.mimeType || 'audio/mp4';
        const audioBlob = new Blob(audioChunksRef.current, { type: blobType });
        const duration = Date.now() - recordingStartTimeRef.current;

        if (duration < MIN_RECORDING_DURATION) {
          onError(i18n.t('Chatroom.recordingTooShort'));
          cleanupRecording();
          return;
        }

        if (duration > MAX_RECORDING_DURATION) {
          onError(i18n.t('Chatroom.recordingTooLong', { maxSeconds: MAX_RECORDING_DURATION / 1000 }));
          cleanupRecording();
          return;
        }

        try {
          const extension =
            blobType.includes('mp4') || blobType.includes('mpeg') ? 'm4a' : blobType.includes('ogg') ? 'ogg' : 'webm';
          const audioFile = new File([audioBlob], `voice_${Date.now()}.${extension}`, {
            type: blobType
          });

          const success = await _dataProvider.uploadFileWithChatMessage(audioFile, groupId, undefined, {
            type: 'audio',
            duration
          });

          if (!success) {
            onError(i18n.t('Chatroom.audioUploadFailed'));
            cleanupRecording();
            return;
          }

          const latestMessageId = messages.length > 0 ? messages[0].id : 0;
          const groupMessages = await _dataProvider.getGroupMessage(
            groupId,
            `to=${latestMessageId + 1}&from=${latestMessageId + 10000}`
          );

          if (!groupMessages || typeof groupMessages !== 'object') {
            return;
          }

          const messagesArray = Array.isArray(groupMessages) ? groupMessages : groupMessages.messages;

          if (Array.isArray(messagesArray) && messagesArray.length > 0) {
            try {
              const newMessages = mapMessage(messagesArray);
              addMessage(newMessages);
            } catch {
              onError(i18n.t('Chatroom.audioProcessError'));
            }
          }
        } catch {
          onError(i18n.t('Chatroom.audioUploadFailed'));
        }

        cleanupRecording();
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingDuration(0);

      recordingIntervalRef.current = setInterval(() => {
        if (!mediaRecorderRef.current || !recordingStartTimeRef.current) {
          setRecordingDuration(0);
          return;
        }

        const actualDuration = Date.now() - recordingStartTimeRef.current;

        if (actualDuration > MAX_RECORDING_DURATION) {
          if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
          }
          cleanupRecording();
          stopRecordingRef.current?.();
          onError(i18n.t('Chatroom.recordingMaxReached'));
          return;
        }

        setRecordingDuration(actualDuration);
      }, 100);
    } catch (error) {
      const err = error as any;
      let errorKey = 'Chatroom.recordingError';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorKey = 'Chatroom.recordingPermissionDenied';
      } else if (err.name === 'NotFoundError') {
        errorKey = 'Chatroom.recordingDeviceNotFound';
      }
      onError(i18n.t(errorKey));
      setIsRecording(false);
      setRecordingDuration(0);
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
    }
  }, [groupId, isRecording, onError, getSupportedMimeType, messages, mapMessage, addMessage, cleanupRecording]);

  const toggleVoiceMode = useCallback(() => {
    setIsVoiceMode((prev) => !prev);
  }, []);

  const formatDuration = useCallback((milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const ms = Math.floor((milliseconds % 1000) / 100);
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
    }
    return `${secs}.${ms}`;
  }, []);

  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch {
          // Error stopping recorder on cleanup
        }
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return {
    isRecording,
    recordingDuration,
    isVoiceMode,
    startRecording,
    stopRecording,
    toggleVoiceMode,
    formatDuration
  };
};
